import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Disclosure,
  Modal,
  Toast,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import LoadingState from "../../../components/LoadingState/index.js";
import EmptyState from "../../../components/EmptyState/index.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal.js";
import { CompanyPicker } from "../../../components/CompanyPicker/CompanyPicker.js";
import { type Company } from "../../../api/addressBook.service.js";
import { useQuery } from "../../../hooks/useApi.js";
import {
  accountRemediationService,
  type BrokenOrg,
  type CompanyHeldResult,
} from "../../../api/accountRemediation.service.js";
import {
  mapOutcomeToState,
  mapOutcomeToSentence,
  formatRequestFailure,
  isCompanyAlreadyHeld,
  buildHeldCompanyRefusal,
  combineDuplicateSignals,
  BROKEN_ACCOUNTS_COLUMN_HEADERS,
  type OutcomeState,
  type RequestFailureDetail,
  type DuplicateSignal,
} from "./remediation.js";
import { groupAccountsByCompany, type AccountGroup } from "./remediationGrouping.js";
import { buildExportRows, EXPORT_COLUMNS } from "./remediationExport.js";
import {
  mergeResolvedIntoList,
  type WorklogEntry,
  type MergedAccount,
} from "./remediationWorklog.js";
import { REPOINT_ENABLED, REPOINT_DISABLED_NOTICE } from "./repointRelease.js";
import { exportToCsv } from "../../../utils/csvExport.js";
import modalStyles from "../../../components/Modal/Modal.module.css";
import styles from "./AccountRemediation.module.css";

type RowResult = {
  state: OutcomeState;
  outcome: string;
  companyName: string;
};

type PendingRepoint = {
  org: BrokenOrg;
  company: Company;
};

type DisplayAccount = MergedAccount<BrokenOrg>;

type ViewMode = "by-account" | "by-company";

function companyAnnotation(company: Company): string {
  return company.firmRef ?? company.crmEntityNumber ?? "no reference";
}

function toastTypeFor(state: OutcomeState): ToastType {
  if (state === "done") return "success";
  if (state === "needs-attention") return "warning";
  return "error";
}

function outcomeClassFor(state: OutcomeState): string {
  if (state === "done") return styles.outcomeDone;
  if (state === "needs-attention") return styles.outcomeNeedsAttention;
  return styles.outcomeError;
}

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

function groupSummary(group: AccountGroup<DisplayAccount>): string {
  const label = group.isNoCompanyGroup ? "No company linked" : `Company ${group.companyId}`;
  const accounts = `${group.accountCount} ${pluralize(group.accountCount, "account", "accounts")}`;
  const users = `${group.totalAffectedUsers} affected ${pluralize(group.totalAffectedUsers, "user", "users")}`;
  return `${label}: ${accounts}, ${users}`;
}

function buildAccountDuplicateIndication(account: DisplayAccount, isDuplicateGroup: boolean) {
  const signals: (DuplicateSignal | null)[] = [
    account.possibleDuplicate
      ? { source: "name-match", detail: account.possibleDuplicate.organisationName }
      : null,
    isDuplicateGroup ? { source: "grouped-company", detail: account.companyId ?? "" } : null,
  ];
  return combineDuplicateSignals(signals);
}

function BrokenAccountsTab() {
  const { data, loading, error, refetch } = useQuery(
    () => accountRemediationService.getDanglingOrgs(),
    [],
  );

  const [viewMode, setViewMode] = useState<ViewMode>("by-account");
  const [detailsOrg, setDetailsOrgState] = useState<BrokenOrg | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [pendingRepoint, setPendingRepoint] = useState<PendingRepoint | null>(null);
  const [results, setResults] = useState<Record<string, RowResult>>({});
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [resolved, setResolved] = useState<WorklogEntry<BrokenOrg>[]>([]);
  const [heldCheck, setHeldCheck] = useState<CompanyHeldResult | null>(null);
  const [heldCheckLoading, setHeldCheckLoading] = useState(false);

  useEffect(() => {
    if (!selectedCompany) {
      setHeldCheck(null);
      setHeldCheckLoading(false);
      return;
    }

    let cancelled = false;
    setHeldCheckLoading(true);
    accountRemediationService
      .getCompaniesHeld([selectedCompany.id])
      .then(response => {
        if (cancelled) return;
        setHeldCheck(response.data[0] ?? null);
      })
      .catch(() => {
        if (!cancelled) setHeldCheck(null);
      })
      .finally(() => {
        if (!cancelled) setHeldCheckLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCompany]);

  const isHeld = Boolean(
    selectedCompany &&
      heldCheck &&
      heldCheck.companyId === selectedCompany.id &&
      isCompanyAlreadyHeld(heldCheck),
  );

  const heldRefusalMessage = useMemo(() => {
    if (!isHeld || !selectedCompany || !heldCheck?.organisation) return null;
    return buildHeldCompanyRefusal({
      companyName: selectedCompany.name,
      holder: heldCheck.organisation,
    });
  }, [isHeld, selectedCompany, heldCheck]);

  const mergedAccounts: DisplayAccount[] = useMemo(
    () => mergeResolvedIntoList(data?.data ?? [], resolved),
    [data, resolved],
  );

  const groups = useMemo(() => groupAccountsByCompany(mergedAccounts), [mergedAccounts]);

  const duplicateGroupByCompanyId = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const group of groups) {
      if (!group.isNoCompanyGroup && group.companyId) {
        map.set(group.companyId, group.isDuplicateGroup);
      }
    }
    return map;
  }, [groups]);

  const openDetails = (org: BrokenOrg) => {
    setSelectedCompany(null);
    setDetailsOrgState(org);
  };

  const handleRowClick = (row: DisplayAccount) => {
    if (row.resolved) return;
    openDetails(row);
  };

  const closeDetails = () => {
    setDetailsOrgState(null);
    setSelectedCompany(null);
  };

  const handleRepointClick = () => {
    if (!REPOINT_ENABLED) return;
    if (!detailsOrg || !selectedCompany || isHeld) return;
    setPendingRepoint({ org: detailsOrg, company: selectedCompany });
  };

  const handleExportCsv = () => {
    const rows = buildExportRows(mergedAccounts, { truncated: data?.truncated ?? false });
    if (rows.length === 0) return;
    exportToCsv(rows, EXPORT_COLUMNS, { filename: "broken-accounts" });
  };

  const handleConfirmRepoint = async () => {
    if (!pendingRepoint) {
      throw new Error("No target organisation selected");
    }

    const { org, company } = pendingRepoint;

    if (heldCheck && heldCheck.companyId === company.id && isCompanyAlreadyHeld(heldCheck)) {
      const refusal = heldCheck.organisation
        ? buildHeldCompanyRefusal({ companyName: company.name, holder: heldCheck.organisation })
        : `${company.name} is already linked to another account.`;
      throw new Error(refusal);
    }

    let response: Awaited<ReturnType<typeof accountRemediationService.safeRepoint>>;
    try {
      response = await accountRemediationService.safeRepoint({
        orgId: org.id,
        targetCompanyId: company.id,
        expectedCurrentCompanyId: org.companyId,
      });
    } catch (caughtError) {
      const requestFailure = caughtError as Error & {
        details?: RequestFailureDetail[];
        requestId?: string;
      };
      const formattedMessage = formatRequestFailure(requestFailure);
      throw new Error(`${formattedMessage} (ref: ${requestFailure.requestId ?? "n/a"})`);
    }

    const outcome = response.data.outcome;
    const state = mapOutcomeToState(outcome);
    setResults(prev => ({
      ...prev,
      [org.id]: { state, outcome, companyName: company.name },
    }));
    setToast({
      message: mapOutcomeToSentence(outcome, {
        organisationName: org.name,
        companyName: company.name,
      }),
      type: toastTypeFor(state),
    });

    const originalIndex = (data?.data ?? []).findIndex(candidate => candidate.id === org.id);
    setResolved(prev => [
      ...prev,
      { account: org, originalIndex: originalIndex === -1 ? prev.length : originalIndex },
    ]);

    setPendingRepoint(null);
    closeDetails();
    await refetch();
  };

  const columns: Column<DisplayAccount>[] = useMemo(
    () => [
      {
        header: BROKEN_ACCOUNTS_COLUMN_HEADERS[0],
        accessor: "name" as keyof DisplayAccount,
        render: (_value: unknown, row: DisplayAccount) => {
          const indication = buildAccountDuplicateIndication(
            row,
            duplicateGroupByCompanyId.get(row.companyId ?? "") ?? false,
          );
          return (
            <div>
              <span>{row.name}</span>
              {row.resolved && (
                <span className={styles.resolvedBadge} data-testid={`resolved-${row.id}`}>
                  Resolved
                </span>
              )}
              {indication.isPossibleDuplicate && (
                <div className={styles.duplicateBadge} data-testid={`duplicate-hint-${row.id}`}>
                  {indication.message}
                </div>
              )}
            </div>
          );
        },
      },
      {
        header: BROKEN_ACCOUNTS_COLUMN_HEADERS[1],
        accessor: "classification" as keyof DisplayAccount,
        render: (value: unknown) => String(value),
      },
      {
        header: BROKEN_ACCOUNTS_COLUMN_HEADERS[2],
        accessor: "status" as keyof DisplayAccount,
        render: (value: unknown) => String(value),
      },
      {
        header: BROKEN_ACCOUNTS_COLUMN_HEADERS[3],
        accessor: "memberCount" as keyof DisplayAccount,
        render: (value: unknown) => String(value ?? 0),
      },
      {
        header: BROKEN_ACCOUNTS_COLUMN_HEADERS[4],
        accessor: "entitlementCount" as keyof DisplayAccount,
        render: (value: unknown) => String(value ?? 0),
      },
      {
        header: BROKEN_ACCOUNTS_COLUMN_HEADERS[5],
        accessor: "id" as keyof DisplayAccount,
        render: (_value: unknown, row: DisplayAccount) => {
          const result = results[row.id];
          if (!result) return null;
          return (
            <div>
              <span
                className={`${styles.outcomeBadge} ${outcomeClassFor(result.state)}`}
                data-testid={`outcome-${row.id}`}
              >
                {result.state}
              </span>
              <div className={styles.resultDetails}>
                {mapOutcomeToSentence(result.outcome, {
                  organisationName: row.name,
                  companyName: result.companyName,
                })}
              </div>
              <small className={styles.resultCode}>{result.outcome}</small>
            </div>
          );
        },
      },
    ],
    [results, duplicateGroupByCompanyId],
  );

  const status = (error as (Error & { status?: number }) | null)?.status;
  const isAuthError = status === 401 || status === 403;

  if (isAuthError) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.content}>
      {loading && <LoadingState message="Loading broken accounts…" />}

      {error && !isAuthError && (
        <EmptyState
          variant="error"
          title="Failed to load broken accounts"
          message={error.message}
          action={{ label: "Retry", onClick: refetch }}
        />
      )}

      {!loading && !error && mergedAccounts.length === 0 && (
        <EmptyState
          variant="empty"
          title="No broken accounts found"
          message="Nothing requires remediation."
        />
      )}

      {!loading && !error && mergedAccounts.length > 0 && (
        <>
          <div className={styles.toolbar}>
            <div className={styles.viewToggle} role="group" aria-label="View accounts by">
              <Button
                variant={viewMode === "by-account" ? "contained" : "outlined"}
                color="default"
                onClick={() => setViewMode("by-account")}
                aria-pressed={viewMode === "by-account"}
              >
                By account
              </Button>
              <Button
                variant={viewMode === "by-company" ? "contained" : "outlined"}
                color="default"
                onClick={() => setViewMode("by-company")}
                aria-pressed={viewMode === "by-company"}
              >
                By company
              </Button>
            </div>
            <Button variant="outlined" color="default" onClick={handleExportCsv}>
              Download CSV
            </Button>
          </div>

          {data?.truncated && (
            <div className={styles.truncationBanner} role="status">
              Showing {data.data.length} of {data.total} broken accounts. The list is truncated.
            </div>
          )}

          {viewMode === "by-account" && (
            <div className={styles.tableContainer}>
              <DataTable
                data={mergedAccounts}
                columns={columns}
                rowKey="id"
                size="small"
                onRowClick={handleRowClick}
                rowClassName={row => (row.resolved ? styles.resolvedRow : "")}
              />
            </div>
          )}

          {viewMode === "by-company" && (
            <div className={styles.groupList}>
              {groups.map(group => (
                <Disclosure
                  key={group.isNoCompanyGroup ? "no-company" : group.companyId}
                  summary={groupSummary(group)}
                  defaultOpen
                >
                  <ul className={styles.groupAccountList}>
                    {group.accounts.map(account => {
                      const indication = buildAccountDuplicateIndication(
                        account,
                        group.isDuplicateGroup,
                      );
                      return (
                        <li
                          key={account.id}
                          className={account.resolved ? styles.resolvedRow : undefined}
                        >
                          {account.resolved ? (
                            <span data-testid={`resolved-${account.id}`}>
                              {account.name}
                              <span className={styles.resolvedBadge}>Resolved</span>
                            </span>
                          ) : (
                            <button
                              type="button"
                              className={styles.groupAccountLink}
                              onClick={() => openDetails(account)}
                            >
                              {account.name}
                            </button>
                          )}
                          {indication.isPossibleDuplicate && (
                            <span
                              className={styles.duplicateBadge}
                              data-testid={`duplicate-hint-${account.id}`}
                            >
                              {indication.message}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </Disclosure>
              ))}
            </div>
          )}
        </>
      )}

      {detailsOrg && !pendingRepoint && (
        <Modal isOpen onClose={closeDetails} title="Re-point organisation" size="medium">
          <div className={styles.inlineForm}>
            <p className={styles.subTitle}>{detailsOrg.name}</p>
            <p className={styles.detailsRow}>
              <strong>Classification:</strong> {detailsOrg.classification}
            </p>
            <p className={styles.detailsRow}>
              <strong>Status:</strong> {detailsOrg.status}
            </p>
            <p className={styles.detailsRow}>
              <strong>Current company ID:</strong> {detailsOrg.companyId ?? "—"}
            </p>
            <p className={styles.detailsRow}>
              <strong>Affected users:</strong> {detailsOrg.memberCount}
            </p>
            <p className={styles.detailsRow}>
              <strong>Live entitlements:</strong> {detailsOrg.entitlementCount}
            </p>

            <CompanyPicker
              value={selectedCompany}
              onChange={setSelectedCompany}
              label="Target company"
              placeholder="Search address-book companies by name…"
              renderOptionAnnotation={company => companyAnnotation(company)}
            />

            {selectedCompany && heldCheckLoading && (
              <p className={styles.heldCheckStatus}>Checking company availability…</p>
            )}

            {heldRefusalMessage && (
              <p className={styles.heldRefusal} role="alert">
                {heldRefusalMessage}
              </p>
            )}

            {!REPOINT_ENABLED && <p className={styles.detailsRow}>{REPOINT_DISABLED_NOTICE}</p>}

            <div className={styles.modalFooter}>
              <Button variant="outlined" color="default" onClick={closeDetails}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="default"
                className={modalStyles.dangerButtonFilled}
                disabled={!REPOINT_ENABLED || !selectedCompany || isHeld}
                onClick={handleRepointClick}
              >
                Re-point
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {pendingRepoint && (
        <ConfirmationModal
          title="Re-point organisation"
          message={`Re-point "${pendingRepoint.org.name}" to "${pendingRepoint.company.name} · ${companyAnnotation(pendingRepoint.company)}".`}
          warningMessage={`This changes the company ${pendingRepoint.org.name} is linked to and cannot be undone from this screen.`}
          confirmButtonText="Re-point"
          confirmButtonColor="error"
          onClose={() => setPendingRepoint(null)}
          onConfirm={handleConfirmRepoint}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default BrokenAccountsTab;
